import React, { useContext, useEffect, useState } from 'react';
import { BrandAndMarketListItem, EntityType, SupplierLocationMappingListConfiguration as GeoGroupsConfiguration } from '../../integrated-client/models';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import {
  BindProperties,
  ConfigurationMethods,
  DEFAULT_FORM_ROW_GUTTER,
  DEFAULT_LIMIT_PAGINATION,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TOTAL_ITEMS,
  ItemTypes,
  SectionKeys,
} from '../../utils/Constants';
import RestClient from '../../integration/RestClient';
import LocalStorage from '../../utils/LocalStorage';
import FiltersLayout from './FiltersLayout';
import TableLayout from './TableLayout';
import { ColumnProps } from 'antd/lib/table';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import Button from 'antd/lib/button/button';
import PermissionsChecker, { CATEGORIES } from '../../utils/PermissionsChecker';
import Dropdown from 'antd/lib/dropdown/dropdown';
import { DownOutlined } from '@ant-design/icons';
import { FiltersData as GeoGroupsFiltersData } from '../../store/state/GeoGroups/FiltersData';
import { FiltersData as AncillaryDistributionFiltersData } from '../../store/state/AncillaryDistribution/FiltersData';
import { TableData as GeoGroupsTableData } from '../../store/state/GeoGroups/TableData';
import { TableData as VccCredentialsTableData } from '../../store/state/VccCredentials/TableData';
import { IConfigurationBindingItem, IItem, IRow } from '../EditorLayout/EditorLayout';
import { usePrevious } from '../../utils/MiscUtils';
import { AuthContext } from '../../auth/AuthContext';
import TableFormatter from '../../utils/TableFormatter';
import { MenuProps } from 'antd/lib/menu';

// to be replaced and to be changed
export type FiltersData = AncillaryDistributionFiltersData | GeoGroupsFiltersData;
export type Configuration = GeoGroupsConfiguration | null;
export type TableData = GeoGroupsTableData | VccCredentialsTableData;

export interface FiltersTableLayoutsWrapperProps {
  layoutFilters:
    | {
        rows: Array<IRow> | [];
        filtersData: FiltersData;
        configurationMethod: ConfigurationMethods;
        configurationBindingItems?: Array<IConfigurationBindingItem>;
        actionClassFilters: any;
        actionClassEditor: any;
      }
    | any;
  layoutTable?: {
    columns: Array<ColumnProps<any>>;
    tableData: TableData;
    actionClassTable: any;
    historyType?: EntityType;
    withDelete?: boolean;
    withCopy?: boolean;
    addButton?: boolean;
    renderOperations?: boolean;
    pageSize?: number;
    openOnRowClick?: boolean;
  };
  sectionKey: SectionKeys;
  path: string;
  menuListProperty: keyof Configuration;
  actionClassEditor?: any;
  bindProperty: BindProperties;
  category?: CATEGORIES;
  history: any;
  isShowAddMenu?: boolean;
  redirectPath?: string[];
}

const FiltersTableLayoutsWrapper: React.FC<FiltersTableLayoutsWrapperProps> = (props) => {
  const { layoutFilters, layoutTable, history, sectionKey, path, menuListProperty, category, bindProperty, isShowAddMenu = true, redirectPath } = props;
  const { filtersData, configurationMethod, configurationBindingItems, actionClassFilters, actionClassEditor, rows } = layoutFilters || {};
  const { columns, tableData, actionClassTable, historyType, withDelete, withCopy, renderOperations, addButton, pageSize, openOnRowClick } = layoutTable || {};
  const { total, currentPage, offset } = filtersData || {};
  const { records } = tableData || {};
  const prevOffset = usePrevious(offset);

  const [configuration, setConfiguration] = useState<any | null>(null);
  const [clearValues, setClearValues] = useState<Array<keyof FiltersData> | null>(null);
  const [currentChangedItemId, setCurrentChangedItemId] = useState<string | null>(null);
  const [clearAgency, setClearAgency] = useState(false);
  const authContext: any = useContext(AuthContext);
  const { userPermissions } = authContext;
  const [sortConfig, setSortConfig] = useState<any>();

  const prepareSearchObj = (sectionKey: SectionKeys, filtersData: FiltersData) => {
    return {
      ...filtersData,
    };
  };
  const renderDefaultDisplayText = (value: any) => (value.label ? value.label : value);
  const renderWithCodeDisplayText = (value: any) => `(${value.code}) ${value.label}`;
  const renderBrandAndMarketDisplayText = (value: BrandAndMarketListItem) => (value ? TableFormatter.formatBrandAndMarketObject(value) : null);
  const renderCheckboxDisplayText = (value: any, item: IItem) => {
    return value ? item.label : null;
  };

  const displayTextRenderMethods = new Map([
    [renderWithCodeDisplayText, ['agency', 'origin', 'destination', 'oneAirLocation']],
    [renderDefaultDisplayText, ['default']],
    [renderBrandAndMarketDisplayText, ['brandAndMarket']],
    [renderCheckboxDisplayText as any, ['showDeleted', 'showDisabled']],
  ]);

  const getDisplayText = (item: IItem, value: any) => {
    if (!item || !value) return null;
    let displayText;
    displayTextRenderMethods.forEach((arrayOfId: Array<string>, renderFunc: any) => {
      if (arrayOfId.includes(item.id)) displayText = renderFunc(value, item);
    });
    return displayText || renderDefaultDisplayText(value);
  };

  const getConfigurationDepends = () =>
    configurationBindingItems.map((configurationBindingItem: IConfigurationBindingItem) => filtersData[configurationBindingItem.id]);

  useEffect(
    () => {
      if (configurationMethod) {
        //to be moved on separate method
        let params: any = {};
        if (configurationBindingItems) {
          configurationBindingItems.forEach((param: IConfigurationBindingItem) =>
            param.field
              ? ((params as any)[param.key] = (filtersData as any)[param.id] ? (filtersData as any)[param.id][param.field] : undefined)
              : ((params as any)[param.key] = (filtersData as any)[param.id])
          );
        }
        (RestClient as any)[sectionKey].list[configurationMethod](configurationBindingItems ? params : undefined)
          .then((result: any) => {
            setConfiguration(result);
          })
          .catch(() => {});
      }
    },
    configurationBindingItems ? getConfigurationDepends() : []
  );

  useEffect(
    () => {
      currentChangedItemId &&
        rows.forEach((row: IRow) => {
          row.items.forEach((item: IItem) => {
            if (item.id === currentChangedItemId && item.hasDependItems) {
              item.hasDependItems.forEach((dependItemId: string) => updateFiltersData({ [dependItemId]: null }));
            }
          });
        });
    },
    currentChangedItemId ? [filtersData[currentChangedItemId]] : undefined
  );

  useEffect(() => {
    if (prevOffset !== undefined && offset !== prevOffset) getData();
    else if (!layoutFilters) getData();
  }, [offset]);

  useEffect(() => {
    if (sortConfig) getData();
  }, [sortConfig]);

  const updateFiltersData = (value: any) => actionClassFilters.updateFiltersData(value);

  const getData = async () => {
    const submitData = prepareSearchObj(sectionKey, filtersData);
    const response = await actionClassTable.fetchData(submitData);
    updatePaginationParams(response);
  };

  const updatePaginationParams = (response: any) => {
    const { total, offset, currentPage } = filtersData || {};
    if (!response.length) {
      actionClassFilters.updateFiltersData({
        total: currentPage === 1 ? total : total - DEFAULT_TOTAL_ITEMS,
        offset: currentPage === 1 ? Number(offset) : Number(offset) - DEFAULT_PAGE_SIZE,
        currentPage: currentPage === 1 ? currentPage : currentPage - 1,
      });
    }
    if (currentPage === total / DEFAULT_PAGE_SIZE - 1 && response.length === DEFAULT_PAGE_SIZE) {
      actionClassFilters.updateFiltersData({
        total: total - DEFAULT_TOTAL_ITEMS,
      });
    }
    if (response.length === DEFAULT_LIMIT_PAGINATION && isNextPage()) {
      actionClassFilters.updateFiltersData({
        total: total + DEFAULT_TOTAL_ITEMS,
      });
    }
  };

  const handlePageChange = (page: number): void => {
    const newOffset = (page - 1) * DEFAULT_PAGE_SIZE;
    const newTotal = DEFAULT_PAGE_SIZE + newOffset;
    updateFiltersData({
      offset: newOffset,
      currentPage: page,
      total: newTotal,
    });
  };

  const handleSearchButtonClick = () => resetPaginationParams().then(() => getData());

  const isNextPage = (): boolean => {
    const { currentPage, total } = filtersData || {};
    return currentPage === total / DEFAULT_PAGE_SIZE;
  };

  const resetPaginationParams = async () => {
    return await updateFiltersData({
      offset: 0,
      currentPage: 1,
      total: DEFAULT_TOTAL_ITEMS,
    });
  };

  const handleAddButtonClick = (boundItem: any) => {
    actionClassEditor?.setBoundItem({ [bindProperty]: boundItem });
    LocalStorage.setItem(bindProperty, {
      boundItem,
      path: path,
    });
    history?.push(`/${path}/new`);
  };

  const handleClearButtonClick = () => {
    actionClassFilters.resetFiltersData();
    let newClearValues: any = [];
    if (rows) {
      rows.forEach((row: IRow) => {
        const autoSuggestIds = row.items.filter((item: IItem) => item?.type === ItemTypes.AutoSuggest).map((item: IItem) => item?.id);
        newClearValues = [...newClearValues, ...autoSuggestIds];
      });
      setClearValues(newClearValues);
    }
  };

  const resetClearValues = () => {
    setClearValues(null);
    setClearAgency(false);
  };

  const handleDeleteOptionClick = async (id: any) => {
    if (typeof id === 'string') {
      await actionClassTable.deleteItem(id);
      getData();
    }
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter?.column?.sorter === true && sorter.field === 'relevance') {
      updateFiltersData({
        orderByRelevanceAscending: sorter.order === 'ascend',
      });
      setSortConfig(sorter);
    }
  };

  //fetching specific object array for menuListProperty
  let itemVal: any;
  rows?.forEach((row: IRow) => {
    if (!itemVal) itemVal = row.items.find((item: IItem) => item?.configurationProperty === menuListProperty);
  });

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    const item = JSON.parse(key);
    handleAddButtonClick(item);
  };

  const menuItems: MenuProps['items'] =
    menuListProperty &&
    configuration &&
    (configuration[menuListProperty] as any)
      .filter((item: any) => item?.effective || item?.allowEdit)
      .map((item: any) => {
        return {
          key: JSON.stringify(item),
          label: item.label || getDisplayText(itemVal, item),
        };
      });

  const renderButtons = () => {
    const isReadOnly = PermissionsChecker.isReadOnly(category, userPermissions);
    return (
      <Row gutter={DEFAULT_FORM_ROW_GUTTER}>
        <Col span="4" id={`button-clear`}>
          <Button block type="primary" onClick={handleClearButtonClick}>
            Clear
          </Button>
        </Col>
        <Col span={isReadOnly ? '16' : '12'}>&nbsp;</Col>
        {!isReadOnly && (
          <Col span="4" id={`button-add`}>
            {isShowAddMenu ? (
              <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={['click']}>
                <PrimaryButton block>
                  Add <DownOutlined />{' '}
                </PrimaryButton>
              </Dropdown>
            ) : (
              <PrimaryButton
                onClick={() => {
                  handleAddButtonClick(configuration?.[menuListProperty]?.[0]);
                }}
                block
              >
                Add
              </PrimaryButton>
            )}
          </Col>
        )}
        <Col span="4" id={`button-search`}>
          <Button block type="primary" onClick={() => handleSearchButtonClick()}>
            Search
          </Button>
        </Col>
      </Row>
    );
  };

  return (
    <>
      {layoutFilters && (
        <FiltersLayout
          sectionKey={sectionKey}
          handleFiltersDataUpdate={updateFiltersData}
          filtersData={filtersData}
          configuration={configuration}
          clearValues={clearValues}
          clearAgency={clearAgency}
          setClearValues={setClearValues}
          resetClearValues={resetClearValues}
          setCurrentChangedId={setCurrentChangedItemId}
          rows={rows}
          title={'Search Parameters'}
          renderButtons={renderButtons}
        />
      )}
      {layoutTable && (
        <TableLayout
          id={sectionKey}
          history={history}
          records={records}
          columns={columns}
          category={category}
          path={path}
          historyType={historyType}
          withDelete={withDelete}
          withCopy={withCopy}
          handlePageChange={handlePageChange}
          handleDeleteOptionClick={handleDeleteOptionClick}
          total={total}
          pageSize={pageSize}
          addButton={addButton}
          currentPage={currentPage}
          handleTableChange={handleTableChange}
          renderOperations={renderOperations}
          redirectPath={redirectPath}
          openOnRowClick={openOnRowClick}
        />
      )}
    </>
  );
};

export default FiltersTableLayoutsWrapper;
